import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { SUPABASE_SYNC_DEBUG } from "@/lib/dataRuntimeFlags";
import { recordRealtimeMetric } from "@/lib/queryObservability";
import {
  OrderingQueuePageService,
  type OrderingPacketPageQuery,
  type OrderingQueueJobEntry,
  type OrderingQueueJobPageQuery,
  type OrderingQueueSummary,
  type PageResponse,
} from "@/services/ordering-queue-page.service";
import type { OrderingPacket } from "@/data/orderingPacket/types";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

function useOrderingPage<T>(
  enabled: boolean | undefined,
  deps: readonly unknown[],
  loader: () => Promise<PageResponse<T>>,
) {
  const [state, setState] = useState<PageResponse<T>>(emptyPage<T>());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!enabled) {
      setState(emptyPage<T>());
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void loader().then((next) => {
      if (cancelled) return;
      setState(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, deps);

  return { ...state, loading };
}

export function useOrderingPacketsPage(query: OrderingPacketPageQuery & { enabled?: boolean }) {
  const result = useOrderingPage<OrderingPacket>(
    query.enabled,
    [query.enabled, query.status, query.page, query.pageSize, query.search, query.vendor, query.refreshToken],
    () => OrderingQueuePageService.getPacketsPage(query),
  );
  return { packets: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useOrderingQueueJobsPage(query: OrderingQueueJobPageQuery & { enabled?: boolean }) {
  const result = useOrderingPage<OrderingQueueJobEntry>(
    query.enabled,
    [query.enabled, query.filter, query.page, query.pageSize, query.search, query.refreshToken],
    () => OrderingQueuePageService.getJobsPage(query),
  );
  return { jobs: result.data, count: result.count, error: result.error, loading: result.loading };
}

export function useOrderingQueueSummary(filters: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<OrderingQueueSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!filters.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    void OrderingQueuePageService.getSummary(filters.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.enabled, filters.refreshToken]);

  return { data, error, loading };
}

export function useOrderingQueueRealtimeRefreshSignal(enabled: boolean) {
  const [refreshToken, setRefreshToken] = useState(0);
  const channelNameRef = useRef(`ordering-queue-server-page-sync-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!enabled) return;
    const bump = (table: string, payload: { eventType?: string; new?: { id?: string | null }; old?: { id?: string | null } }) => {
      const entityId = String(payload.new?.id ?? payload.old?.id ?? "");
      recordRealtimeMetric({
        source: "ordering-queue-server-page",
        event: "exact-refetch",
        table,
        entityId: entityId || undefined,
        detail: SUPABASE_SYNC_DEBUG ? { eventType: payload.eventType } : undefined,
      });
      setRefreshToken((current) => current + 1);
    };

    const channel = supabase
      .channel(channelNameRef.current)
      .on("postgres_changes", { event: "*", schema: "public", table: "ordering_packets" }, (payload) => bump("ordering_packets", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "ordering_vendor_lead_times" }, (payload) => bump("ordering_vendor_lead_times", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "purchase_orders" }, (payload) => bump("purchase_orders", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "po_line_items" }, (payload) => bump("po_line_items", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "jobs" }, (payload) => bump("jobs", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_materials" }, (payload) => bump("job_materials", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "job_allocations" }, (payload) => bump("job_allocations", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "shipments" }, (payload) => bump("shipments", payload as any))
      .on("postgres_changes", { event: "*", schema: "public", table: "receiving_records" }, (payload) => bump("receiving_records", payload as any))
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled]);

  return refreshToken;
}
